/**
 * Tip Jar manager — bridges JavaScript to native StoreKit 2 via WKWebView.
 *
 * Handles fetching product info (localized prices) and processing purchases
 * for the 3 consumable tip tiers. All functions gracefully no-op when the
 * native bridge is unavailable (browser, Android, iOS < 15).
 */

import { schedulePaint } from './paint'

// ─── Constants ──────────────────────────────────────────────────────────────

const TIP_PRODUCT_IDS = ['tip_small', 'tip_medium', 'tip_large'] as const

type TipProductId = (typeof TIP_PRODUCT_IDS)[number]

/** Fallback display info used when native product fetch hasn't completed. */
const FALLBACK_PRODUCTS: TipProduct[] = [
    { id: 'tip_small', displayName: 'Small Tip', displayPrice: '$0.99', price: 0.99 },
    { id: 'tip_medium', displayName: 'Nice Tip', displayPrice: '$2.99', price: 2.99 },
    { id: 'tip_large', displayName: 'Generous Tip', displayPrice: '$4.99', price: 4.99 },
]

// ─── Types ──────────────────────────────────────────────────────────────────

interface TipProduct {
    id: string
    displayName: string
    displayPrice: string
    price: number
}

type PurchaseStatus = 'idle' | 'purchasing' | 'success' | 'cancelled' | 'error' | 'pending'

// ─── Module state ───────────────────────────────────────────────────────────

let _products: TipProduct[] = []
let _productsLoaded = false
let _purchaseStatus: PurchaseStatus = 'idle'
let _purchaseMessage: string | null = null

// ─── State accessors ────────────────────────────────────────────────────────

/**
 * Get the list of available tip products.
 * Always returns exactly 3 products (one per tier). Uses fetched App Store
 * data when available, falling back to hardcoded info for any tier that
 * wasn't returned by StoreKit (e.g. products not yet configured in ASC).
 */
export function getTipProducts(): TipProduct[] {
    if (!_productsLoaded) return FALLBACK_PRODUCTS
    return FALLBACK_PRODUCTS.map((fallback) => {
        const fetched = _products.find((p) => p.id === fallback.id)
        return fetched ?? fallback
    })
}

/** Whether products have been fetched from the App Store. */
export function isTipJarAvailable(): boolean {
    return !!getNativeBridge()
}

/** Current purchase status for UI feedback. */
export function getPurchaseStatus(): PurchaseStatus {
    return _purchaseStatus
}

/** Optional message from last purchase attempt (e.g. error description). */
function getPurchaseMessage(): string | null {
    return _purchaseMessage
}

/** Reset purchase status back to idle (e.g. when leaving tip jar screen). */
export function resetPurchaseStatus(): void {
    _purchaseStatus = 'idle'
    _purchaseMessage = null
}

// ─── Native bridge helpers ──────────────────────────────────────────────────

function getNativeBridge(): any | undefined {
    try {
        const wk = (window as any).webkit
        return wk?.messageHandlers?.storeKit
    } catch {
        return undefined
    }
}

function postToNative(message: Record<string, unknown>): void {
    const bridge = getNativeBridge()
    if (bridge) {
        bridge.postMessage(message)
    }
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Fetch product info from the App Store.
 * Call on app boot or when opening the tip jar screen.
 * Sets up JS callbacks and requests product data from native.
 */
export function fetchTipProducts(): void {
    if (!getNativeBridge()) return // Set up callback for native → JS product info
    ;(window as any).__onStoreKitProducts = (
        products: { id: string; displayName: string; displayPrice: string; price: string }[]
    ) => {
        _products = products.map((p) => ({
            id: p.id,
            displayName: p.displayName,
            displayPrice: p.displayPrice,
            price: parseFloat(p.price) || 0,
        }))
        // Sort by price ascending to match our expected order
        _products.sort((a, b) => a.price - b.price)
        _productsLoaded = _products.length > 0
        schedulePaint()
    }

    postToNative({ action: 'fetchProducts', productIds: [...TIP_PRODUCT_IDS] })
}

/**
 * Initiate a purchase for the given product ID.
 * Purchase result will arrive via the __onStoreKitPurchase callback.
 */
export function purchaseTip(productId: string): void {
    if (!getNativeBridge()) return

    _purchaseStatus = 'purchasing'
    _purchaseMessage = null
    schedulePaint()

    // Set up callback for native → JS purchase result
    ;(window as any).__onStoreKitPurchase = (result: {
        productId: string
        status: string
        message: string | null
    }) => {
        _purchaseStatus = result.status as PurchaseStatus
        _purchaseMessage = result.message
        schedulePaint()
    }

    postToNative({ action: 'purchase', productId })
}
